import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProjectModal = ({ project, isOpen, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!project) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 bg-background/95 backdrop-blur-sm z-[150]"
            onClick={onClose}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ duration: 0.3, type: 'spring' }}
            className="fixed inset-4 md:inset-8 lg:inset-16 z-[160] overflow-y-auto"
          >
            <div className="bg-card rounded-xl shadow-2xl border border-border max-w-5xl mx-auto">
              <div className="sticky top-0 bg-card border-b border-border p-4 md:p-6 flex items-center justify-between z-10 rounded-t-xl">
                <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold line-clamp-1">
                  {project?.title}
                </h2>
                <button
                  onClick={onClose}
                  className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-colors"
                  aria-label="Close modal"
                >
                  <Icon name="X" size={24} color="currentColor" />
                </button>
              </div>

              <div className="p-4 md:p-6 lg:p-8">
                <div className="relative h-64 md:h-80 lg:h-96 rounded-xl overflow-hidden mb-6 md:mb-8">
                  <Image
                    src={project?.thumbnail}
                    alt={project?.thumbnailAlt}
                    className="w-full h-full object-cover"
                  />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8 mb-6 md:mb-8">
                  <div className="lg:col-span-2 space-y-6">
                    <div>
                      <h3 className="text-lg md:text-xl font-semibold mb-3 flex items-center gap-2">
                        <Icon name="FileText" size={24} color="var(--color-primary)" />
                        Project Overview
                      </h3>
                      <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                        {project?.fullDescription}
                      </p>
                    </div>

                    <div>
                      <h3 className="text-lg md:text-xl font-semibold mb-3 flex items-center gap-2">
                        <Icon name="Target" size={24} color="var(--color-secondary)" />
                        Key Objectives
                      </h3>
                      <ul className="space-y-2">
                        {project?.objectives?.map((objective, index) => (
                          <li key={index} className="flex items-start gap-3">
                            <Icon name="CheckCircle2" size={20} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                            <span className="text-sm md:text-base text-muted-foreground">{objective}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-lg md:text-xl font-semibold mb-3 flex items-center gap-2">
                        <Icon name="MessageSquare" size={24} color="var(--color-accent)" />
                        Client Testimonial
                      </h3>
                      <div className="bg-muted rounded-lg p-4 md:p-6">
                        <p className="text-sm md:text-base italic text-foreground mb-4">
                          "{project?.testimonial?.quote}"
                        </p>
                        <div className="flex items-center gap-3">
                          <Image
                            src={project?.testimonial?.clientImage}
                            alt={project?.testimonial?.clientImageAlt}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                          <div>
                            <div className="font-semibold text-sm md:text-base">{project?.testimonial?.clientName}</div>
                            <div className="text-xs md:text-sm text-muted-foreground">{project?.testimonial?.clientRole}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-6">
                    <div className="bg-muted rounded-lg p-4 md:p-6">
                      <h3 className="text-base md:text-lg font-semibold mb-4">Project Details</h3>
                      <div className="space-y-3">
                        <div>
                          <div className="text-xs md:text-sm text-muted-foreground mb-1">Category</div>
                          <div className="font-medium text-sm md:text-base">{project?.category}</div>
                        </div>
                        <div>
                          <div className="text-xs md:text-sm text-muted-foreground mb-1">Year</div>
                          <div className="font-medium text-sm md:text-base">{project?.year}</div>
                        </div>
                        <div>
                          <div className="text-xs md:text-sm text-muted-foreground mb-1">Duration</div>
                          <div className="font-medium text-sm md:text-base">{project?.duration}</div>
                        </div>
                        <div>
                          <div className="text-xs md:text-sm text-muted-foreground mb-1">Team Size</div>
                          <div className="font-medium text-sm md:text-base">{project?.teamSize}</div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-muted rounded-lg p-4 md:p-6">
                      <h3 className="text-base md:text-lg font-semibold mb-4">Technologies Used</h3>
                      <div className="flex flex-wrap gap-2">
                        {project?.technologies?.map((tech, index) => (
                          <span
                            key={index}
                            className="px-3 py-1.5 bg-card text-xs md:text-sm rounded-md font-medium"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div className="bg-muted rounded-lg p-4 md:p-6">
                      <h3 className="text-base md:text-lg font-semibold mb-4">Project Metrics</h3>
                      <div className="space-y-4">
                        {project?.metrics?.map((metric, index) => (
                          <div key={index}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs md:text-sm text-muted-foreground">{metric?.label}</span>
                              <span className="text-sm md:text-base font-semibold text-primary">{metric?.value}</span>
                            </div>
                            <div className="h-2 bg-card rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${metric?.percentage}%` }}
                                transition={{ duration: 1, delay: 0.2 }}
                                className="h-full bg-primary rounded-full"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-6 border-t border-border">
                  <Button
                    variant="default"
                    fullWidth
                    iconName="ExternalLink"
                    iconPosition="right"
                    onClick={() => window.open(project?.liveUrl, '_blank')}
                  >
                    View Live Project
                  </Button>
                  <Button
                    variant="outline"
                    fullWidth
                    iconName="Github"
                    iconPosition="left"
                    onClick={() => window.open(project?.githubUrl, '_blank')}
                  >
                    View Code
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ProjectModal;